import { Moon, Sun, Monitor } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme, type Theme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";

const iconVariants = {
  initial: { scale: 0, rotate: -90, opacity: 0 },
  animate: { scale: 1, rotate: 0, opacity: 1 },
  exit: { scale: 0, rotate: 90, opacity: 0 },
};

const themeLabels: Record<Theme, string> = {
  light: "common.theme_light",
  dark: "common.theme_dark",
  system: "common.theme_system",
};

export function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const currentLabel = t(themeLabels[theme]);

  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-input bg-background text-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      aria-label={`${t("accessibility.toggle_theme")} (${currentLabel})`}
      title={`${t("accessibility.toggle_theme")}: ${currentLabel}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {theme === "light" && (
          <motion.span
            key="sun"
            variants={iconVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute"
          >
            <Sun className="h-[1.15rem] w-[1.15rem]" />
          </motion.span>
        )}

        {theme === "dark" && (
          <motion.span
            key="moon"
            variants={iconVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute"
          >
            <Moon className="h-[1.15rem] w-[1.15rem]" />
          </motion.span>
        )}

        {theme === "system" && (
          <motion.span
            key="monitor"
            variants={iconVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="absolute"
          >
            <Monitor className="h-[1.15rem] w-[1.15rem]" />
          </motion.span>
        )}
      </AnimatePresence>
      <span className="sr-only">{currentLabel}</span>
    </button>
  );
}

export default ThemeToggle;
